import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, CalendarDays, Heart, LogOut, PlusCircle, ShieldCheck, MessagesSquare, Mail, UserRound } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Header = ({ query, onQueryChange }) => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [local, setLocal] = useState(query || "");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    apiFetch("/dm/unread").then((r) => setUnread(r.unread)).catch(() => {});
  }, [user]);

  const submit = (e) => {
    e.preventDefault();
    if (onQueryChange) onQueryChange(local);
    else navigate(`/calendar?q=${encodeURIComponent(local)}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-neutral-200 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/calendar" data-testid="logo-link" className="flex shrink-0 items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#B8FF2E] text-[#14210A]">
            <CalendarDays className="h-[18px] w-[18px]" />
          </span>
          <span className="hidden text-[17px] font-extrabold tracking-tight text-neutral-900 sm:block">
            Portland Tech
          </span>
        </Link>

        <form
          onSubmit={submit}
          className="mx-auto flex w-full max-w-md items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.05)]"
        >
          <input
            data-testid="search-input"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            placeholder="Search talks, workshops, hack nights"
            className="w-full bg-transparent text-sm font-medium text-neutral-900 outline-none placeholder:text-neutral-400"
          />
          <button
            type="submit"
            data-testid="search-submit"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#B8FF2E] text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
          >
            <Search className="h-4 w-4" />
          </button>
        </form>

        <div className="flex shrink-0 items-center gap-1">
          <Link
            to="/calendar"
            data-testid="nav-calendar"
            className="hidden rounded-full px-3 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-100 md:block"
          >
            Calendar
          </Link>
          <Link
            to="/forum"
            data-testid="nav-forum"
            className="hidden rounded-full px-3 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-100 md:block"
          >
            Forum
          </Link>
          <Link
            to="/communities"
            data-testid="nav-communities"
            className="hidden rounded-full px-3 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-100 lg:block"
          >
            Communities
          </Link>
          <Link
            to="/directory"
            data-testid="nav-directory"
            className="hidden rounded-full px-3 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-100 md:block"
          >
            Members
          </Link>
          <Link
            to="/messages"
            data-testid="nav-messages"
            className="relative hidden rounded-full px-3 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-100 md:block"
          >
            Messages
            {unread > 0 && (
              <span
                data-testid="nav-unread-badge"
                className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#B8FF2E] px-1.5 text-[10px] font-bold text-[#14210A]"
              >
                {unread}
              </span>
            )}
          </Link>
          {(user?.role === "leader" || user?.role === "admin") && (
            <Link
              to="/organize"
              data-testid="organize-link"
              className="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-100 md:flex"
            >
              <PlusCircle className="h-4 w-4" /> Add event
            </Link>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 rounded-full border border-neutral-200 py-1.5 pl-3 pr-1.5 transition-shadow duration-200 hover:shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
              >
                <Menu className="h-4 w-4 text-neutral-700" />
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" />
                ) : (
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-neutral-800 text-xs font-bold text-white">
                    {user.name?.[0] || "U"}
                  </span>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl bg-white p-1">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-neutral-900">{user.name}</p>
                  <p className="text-xs text-neutral-500">{user.email}</p>
                  <span className="mt-1 inline-block rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-neutral-600">
                    {user.role}
                  </span>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>
                  <UserRound className="mr-2 h-4 w-4" /> My profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-forum" onClick={() => navigate("/forum")}>
                  <MessagesSquare className="mr-2 h-4 w-4" /> Member forum
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate("/messages")}>
                  <Mail className="mr-2 h-4 w-4" /> Messages
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-saved" onClick={() => navigate("/saved")}>
                  <Heart className="mr-2 h-4 w-4" /> Saved events
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-rsvps" onClick={() => navigate("/dashboard")}>
                  <CalendarDays className="mr-2 h-4 w-4" /> My RSVPs
                </DropdownMenuItem>
                {(user.role === "leader" || user.role === "admin") && (
                  <DropdownMenuItem data-testid="menu-host" onClick={() => navigate("/host")}>
                    <CalendarDays className="mr-2 h-4 w-4" /> Host dashboard
                  </DropdownMenuItem>
                )}
                {user.role === "leader" && (
                  <DropdownMenuItem data-testid="menu-organize" onClick={() => navigate("/organize")}>
                    <PlusCircle className="mr-2 h-4 w-4" /> Organizer tools
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <>
                    <DropdownMenuItem data-testid="menu-organize-admin" onClick={() => navigate("/organize")}>
                      <PlusCircle className="mr-2 h-4 w-4" /> Organizer tools
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <ShieldCheck className="mr-2 h-4 w-4" /> Leaders & digest
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={login}
              data-testid="login-btn"
              className="rounded-full bg-neutral-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-700"
            >
              Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
